import { html, nothing } from "lit";
import type { EventLogEntry } from "../app-events.ts";
import { formatEventPayload } from "../presenter.ts";

export type DebugProps = {
  loading: boolean;
  status: Record<string, unknown> | null;
  health: Record<string, unknown> | null;
  lifecheck: unknown;
  eventLog: EventLogEntry[];
  callMethod: string;
  callParams: string;
  callResult: string | null;
  callError: string | null;
  onCallMethodChange: (next: string) => void;
  onCallParamsChange: (next: string) => void;
  onRefresh: () => void;
  onCall: () => void;
  onClearEventLog: () => void;
};

export function renderDebug(props: DebugProps) {
  const securityAudit =
    props.status && typeof props.status === "object"
      ? (props.status as { securityAudit?: { summary?: Record<string, number> } }).securityAudit
      : null;
  const securitySummary = securityAudit?.summary ?? null;
  const critical = securitySummary?.critical ?? 0;
  const warn = securitySummary?.warn ?? 0;
  const info = securitySummary?.info ?? 0;
  const securityTone = critical > 0 ? "danger" : warn > 0 ? "warn" : "success";
  const securityLabel =
    critical > 0 ? `${critical} critical` : warn > 0 ? `${warn} warnings` : "No critical issues";

  return html`
    ${securitySummary
      ? html`<div class="callout ${securityTone}" style="margin-bottom: 16px;">
          <strong>Security audit:</strong> ${securityLabel}${info > 0 ? ` · ${info} info` : ""}.
          Run <span class="mono">i-gloobot security audit --deep</span> for details.
        </div>`
      : nothing}

    <section class="grid grid-cols-2">
      <div class="card">
        <div class="row" style="justify-content: space-between;">
          <div>
            <div class="card-title">Snapshots</div>
            <div class="card-sub">Raw JSON from gateway status, health, and lifecheck.</div>
          </div>
          <button class="btn" ?disabled=${props.loading} @click=${props.onRefresh}>
            ${props.loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
        <div class="stack" style="margin-top: 12px;">
          <details class="cfg-object">
            <summary class="cfg-object__header">Status</summary>
            <pre class="code-block">${JSON.stringify(props.status ?? {}, null, 2)}</pre>
          </details>
          <details class="cfg-object">
            <summary class="cfg-object__header">Health</summary>
            <pre class="code-block">${JSON.stringify(props.health ?? {}, null, 2)}</pre>
          </details>
          <details class="cfg-object">
            <summary class="cfg-object__header">Last lifecheck</summary>
            <pre class="code-block">${JSON.stringify(props.lifecheck ?? {}, null, 2)}</pre>
          </details>
        </div>
      </div>

      <div class="card">
        <div class="card-title">Manual RPC</div>
        <div class="card-sub">Send a raw gateway method with JSON params.</div>
        <div class="form-grid" style="margin-top: 16px;">
          <label class="field">
            <span>Method</span>
            <input
              .value=${props.callMethod}
              @input=${(e: Event) => props.onCallMethodChange((e.target as HTMLInputElement).value)}
              placeholder="system-presence"
            />
          </label>
          <label class="field">
            <span>Params (JSON)</span>
            <textarea
              .value=${props.callParams}
              @input=${(e: Event) =>
                props.onCallParamsChange((e.target as HTMLTextAreaElement).value)}
              rows="6"
              placeholder='{"channel": "telegram"}'
            ></textarea>
          </label>
        </div>
        <div class="row" style="margin-top: 12px;">
          <button class="btn primary" @click=${props.onCall}>Call</button>
        </div>
        ${
          props.callError
            ? html`<div class="callout danger" style="margin-top: 12px;">
              ${props.callError}
            </div>`
            : nothing
        }
        ${
          props.callResult
            ? html`<details class="cfg-object" style="margin-top: 12px;">
                <summary class="cfg-object__header">Result</summary>
                <pre class="code-block">${props.callResult}</pre>
              </details>`
            : nothing
        }
      </div>
    </section>

    <section class="card" style="margin-top: 18px;">
      <div class="row" style="justify-content: space-between; align-items: center;">
        <div>
          <div class="card-title">Event Log</div>
          <div class="card-sub">
            Latest gateway events${props.eventLog.length > 0 ? ` (${props.eventLog.length})` : ""}.
          </div>
        </div>
        ${
          props.eventLog.length > 0
            ? html`<button class="btn btn--sm" @click=${props.onClearEventLog}>Clear</button>`
            : nothing
        }
      </div>
      ${
        props.eventLog.length === 0
          ? html`<div class="muted" style="margin-top: 12px">No events yet.</div>`
          : html`<div class="list" style="margin-top: 12px;">
              ${props.eventLog.map(
                (evt) => html`
                  <div class="list-item">
                    <div class="list-main">
                      <div class="list-title">${evt.event}</div>
                      <div class="list-sub">${new Date(evt.ts).toLocaleTimeString()}</div>
                    </div>
                    <div class="list-meta">
                      <pre class="code-block">${formatEventPayload(evt.payload)}</pre>
                    </div>
                  </div>
                `,
              )}
            </div>`
      }
    </section>
  `;
}
